import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import client from "@/lib/api";
import { StatusBadge } from "@/components/billing/StatusBadge";
import { useGuarded, inputCls, fmtDate, Stat, SubNav } from "@/components/billing/shared";
import { Audit } from "@/components/billing/BillingCenter";

function Overview() {
  const [d, setD] = useState(null);
  useEffect(() => { client.get("/admin/treasury/overview").then((r) => setD(r.data)); }, []);
  if (!d) return null;
  const i = d.internal;
  return (
    <div data-testid="treasury-overview" className="space-y-6">
      <div className="rounded-xl border border-white/10 p-4">
        <p className="text-[10px] font-mono-cl uppercase tracking-[0.14em] text-zinc-500">Custody balances (provider-reported)</p>
        {d.custody.available ? <pre className="text-sm">{JSON.stringify(d.custody.balances, null, 2)}</pre>
          : <p data-testid="custody-unavailable" className="text-sm text-zinc-300 mt-1">Unavailable: {d.custody.reason}</p>}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Gross revenue (internal)" value={`$${i.gross_revenue_usd}`} testid="tr-gross" />
        <Stat label="Pending payments" value={`${i.pending_payments} · $${i.pending_usd}`} testid="tr-pending" />
        <Stat label="Open reconciliation exceptions" value={d.reconciliation.open_exceptions} testid="tr-exceptions" />
        <Stat label="Last reconciliation" value={fmtDate(d.reconciliation.last_run_at)} testid="tr-last-run" />
      </div>
      <div className="grid md:grid-cols-3 gap-3">
        {[["Crypto received (internal)", i.crypto_received], ["Withdrawn (recorded)", i.withdrawn], ["Analyst revenue attribution (USD)", d.analyst_attribution_usd]].map(([t, obj]) => (
          <div key={t} className="rounded-xl border border-white/10 p-4"><p className="text-[10px] font-mono-cl uppercase tracking-[0.14em] text-zinc-500 mb-2">{t}</p>
            {Object.entries(obj).map(([k, v]) => <p key={k} className="text-sm flex justify-between"><span>{k.toUpperCase?.() === k ? k : k}</span><b>{v}</b></p>)}
            {Object.keys(obj).length === 0 && <p className="text-sm text-zinc-600">None</p>}</div>))}
      </div>
      <p className="text-xs text-zinc-500">{d.attribution_note} Internal figures come from verified payments; they are not the custody balance. Payouts from the app are disabled.</p>
    </div>
  );
}

const ACTIONS = { PENDING_APPROVAL: [["approve", "Approve"], ["reject", "Reject"], ["cancel", "Cancel"]],
  APPROVED_FOR_MANUAL_EXECUTION: [["record", "Record NOWPayments reference"]], PROCESSING: [["complete", "Confirm completed"], ["fail", "Mark failed"]] };

function Withdrawals() {
  const [rows, setRows] = useState([]);
  const [f, setF] = useState({ amount: "", asset: "usdtbsc", network: "BNB Chain (BEP20)", destination_ref: "", purpose: "" });
  const [act, setAct] = useState({});
  const [run, dialog] = useGuarded();
  const load = () => client.get("/admin/treasury/withdrawals").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const create = () => run(async () => { await client.post("/admin/treasury/withdrawals", f); toast.success("Request submitted for approval"); load(); });
  const doAction = (w, a) => run(async () => {
    await client.post(`/admin/treasury/withdrawals/${w.id}/${a}`, { reason: act[w.id]?.reason || "", provider_reference: act[w.id]?.ref || "" });
    toast.success("Updated"); load();
  });
  return (
    <div data-testid="treasury-withdrawals" className="space-y-6">
      <div className="rounded-xl border border-white/10 p-4 text-xs text-zinc-400">
        Withdrawals are executed only inside NOWPayments (with its 2FA, IP and wallet allowlists). This module records the request, a separate approval, the provider reference and an independent completion check. It never moves funds.
      </div>
      <div className="rounded-xl border border-white/10 p-4 grid md:grid-cols-2 gap-3">
        <p className="md:col-span-2 font-semibold text-sm">New withdrawal request</p>
        <input data-testid="wd-amount" className={inputCls} placeholder="Amount" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} />
        <select data-testid="wd-asset" className={inputCls} value={f.asset} onChange={(e) => setF({ ...f, asset: e.target.value, network: e.target.selectedOptions[0].text })}>
          {[["usdtbsc", "BNB Chain (BEP20)"], ["usdterc20", "Ethereum (ERC20)"], ["usdtsol", "Solana (SPL)"], ["usdttrc20", "Tron (TRC20)"]].map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
        <input data-testid="wd-destination" className={inputCls} placeholder="Allowlisted wallet label / reference in NOWPayments" value={f.destination_ref} onChange={(e) => setF({ ...f, destination_ref: e.target.value })} />
        <input data-testid="wd-purpose" className={inputCls} placeholder="Purpose" value={f.purpose} onChange={(e) => setF({ ...f, purpose: e.target.value })} />
        <button data-testid="wd-submit" onClick={create} className="md:col-span-2 py-2.5 rounded-full bg-[#ccff00] text-black text-sm font-semibold">Submit for approval</button>
      </div>
      {rows.map((w) => (
        <div key={w.id} data-testid={`wd-row-${w.id}`} className="rounded-xl border border-white/10 p-4">
          <div className="flex flex-wrap gap-3 items-center"><b>{w.amount} {w.asset.toUpperCase()}</b><span className="text-xs text-zinc-500">{w.network} → {w.destination_ref}</span><StatusBadge status={w.status} />
            {w.provider_reference && <span className="text-xs font-mono-cl text-zinc-400">ref {w.provider_reference}</span>}</div>
          <p className="text-xs text-zinc-500 mt-1">{w.purpose} · requested {fmtDate(w.created_at)}</p>
          {ACTIONS[w.status] && (
            <div className="flex flex-wrap gap-2 mt-3">
              <input data-testid="wd-action-reason" className={inputCls + " flex-1 min-w-[160px] text-xs"} placeholder="Reason" onChange={(e) => setAct({ ...act, [w.id]: { ...act[w.id], reason: e.target.value } })} />
              {w.status === "APPROVED_FOR_MANUAL_EXECUTION" && <input data-testid="wd-ref" className={inputCls + " text-xs"} placeholder="NOWPayments withdrawal ID" onChange={(e) => setAct({ ...act, [w.id]: { ...act[w.id], ref: e.target.value } })} />}
              {ACTIONS[w.status].map(([a, l]) => <button key={a} data-testid={`wd-${a}`} onClick={() => doAction(w, a)} className="px-3 py-1.5 rounded-full border border-white/15 text-xs">{l}</button>)}
            </div>
          )}
          {w.events?.map((e) => <p key={e.id} className="text-[11px] text-zinc-600 mt-1">{fmtDate(e.created_at)} · {e.action}: {e.from} → {e.to} · {e.reason}</p>)}
        </div>
      ))}
      {dialog}
    </div>
  );
}

function FinanceStaff() {
  const [rows, setRows] = useState([]);
  const [run, dialog] = useGuarded();
  const load = () => client.get("/admin/billing/staff").then((r) => setRows(r.data)).catch(() => setRows(null));
  useEffect(() => { load(); }, []);
  if (rows === null) return <p className="text-sm text-zinc-500">Only super admins can manage finance roles.</p>;
  const ROLES = ["FINANCE_ADMIN", "TREASURY_APPROVER", "TREASURY_OPERATOR", "BILLING_MANAGER"];
  const toggle = (u, r) => run(async () => {
    const cur = u.finance_roles || [];
    const roles = cur.includes(r) ? cur.filter((x) => x !== r) : [...cur, r];
    const reason = window.prompt("Reason for this role change?");
    if (!reason) return;
    await client.post(`/admin/billing/staff/${u.id}/roles`, { roles, reason }); load();
  });
  const fin = (u) => run(async () => { const reason = window.prompt("Reason?"); if (!reason) return; await client.post(`/admin/billing/staff/${u.id}/analyst-financials`, { enabled: !u.analyst_financials, reason }); load(); });
  return (
    <div data-testid="finance-staff">
      <p className="text-xs text-zinc-500 mb-3">Super admins don't approve withdrawals by default. Grant TREASURY_APPROVER explicitly, and no one can approve their own request.</p>
      {rows.map((u) => (
        <div key={u.id} className="flex flex-wrap items-center gap-2 py-2 border-b border-white/5 text-sm">
          <span className="flex-1 min-w-[200px]">{u.name} <span className="text-xs text-zinc-500">{u.email} · {u.role}</span></span>
          {ROLES.map((r) => <button key={r} data-testid={`role-${u.id}-${r}`} onClick={() => toggle(u, r)} className={`px-2.5 py-1 rounded-full text-[10px] border ${u.finance_roles?.includes(r) ? "border-[#ccff00] text-[#ccff00]" : "border-white/10 text-zinc-500"}`}>{r}</button>)}
          {u.role === "analyst" && <button data-testid={`analyst-fin-${u.id}`} onClick={() => fin(u)} className={`px-2.5 py-1 rounded-full text-[10px] border ${u.analyst_financials ? "border-[#ccff00] text-[#ccff00]" : "border-white/10 text-zinc-500"}`}>ANALYST REVENUE VIEW</button>}
        </div>
      ))}
      {dialog}
    </div>
  );
}

export function Treasury({ perms = [] }) {
  const [s, setS] = useState("overview");
  const items = [["overview", "Overview"], ["withdrawals", "Withdrawal Requests"],
    ...(perms.includes("finance.roles.manage") ? [["roles", "Finance Roles"]] : []), ["audit", "Treasury Audit Logs"]];
  return (
    <div data-testid="treasury">
      <h2 className="font-display text-2xl font-bold mb-4">Treasury</h2>
      <SubNav prefix="treasury-nav" value={s} onChange={setS} items={items} />
      {s === "overview" && <Overview />}
      {s === "withdrawals" && <Withdrawals />}
      {s === "roles" && <FinanceStaff />}
      {s === "audit" && <Audit domain="treasury" />}
    </div>
  );
}

export function AnalystAnalytics() {
  const [d, setD] = useState(null);
  useEffect(() => { client.get("/billing/analyst/stats").then((r) => setD(r.data)); }, []);
  if (!d) return null;
  return (
    <div data-testid="analyst-analytics" className="space-y-8">
      <h2 className="font-display text-2xl font-bold">Subscriber analytics</h2>
      {d.analysts.map((a) => (
        <div key={a.analyst.id} className="rounded-xl border border-white/10 p-5">
          <p className="font-semibold mb-3">{a.analyst.name}</p>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <Stat label="Active subscribers" value={a.active_subscribers} testid="aa-active" />
            <Stat label="New (30d)" value={a.new_subscribers_30d} />
            <Stat label="Expired" value={a.expired_subscribers} />
            <Stat label="Renewal rate" value={a.renewal_rate === null ? "-" : `${Math.round(a.renewal_rate * 100)}%`} />
            <Stat label="Churn rate" value={a.churn_rate === null ? "-" : `${Math.round(a.churn_rate * 100)}%`} />
          </div>
          <div className="flex gap-2 mt-4 overflow-x-auto">{a.monthly.map((m) => (
            <div key={m.month} className="min-w-[90px] rounded-lg border border-white/5 p-2 text-xs"><p className="text-zinc-500">{m.month}</p><p>{m.payments} payments</p>{d.show_financials && <p className="text-[#ccff00]">${m.gross_usd}</p>}</div>))}</div>
          {d.show_financials ? <p className="text-xs text-zinc-500 mt-3">Gross USDT subscription revenue (6 months): ${a.gross_usd}. {a.fees_note}. Attribution only, not a payout.</p>
            : <p className="text-xs text-zinc-600 mt-3">Revenue figures are visible once an admin enables revenue access for your account.</p>}
        </div>
      ))}
      {d.analysts.length === 0 && <p className="text-sm text-zinc-500">No analyst profile linked to your account.</p>}
    </div>
  );
}
